import React, { useMemo } from "react";
import { useMount } from "react-use";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";

import { MultiLayerEditProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProvider";
import {
  DeleteButton,
  LineStringButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  ToolbarConfig,
  TopologicalButton,
  UndoButton,
} from "fond/map/Toolbar";
import { EditMode, setEditMode } from "fond/project/redux";
import { ISGLayerIds } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

import DataPrepDrawContent from "./BlockersDrawContent";

const allToolConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="selectPolygon" />],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<LineStringButton key="drawLine" />],
  },
  {
    id: "miscTools",
    type: "action",
    items: [<SnappingButton key="snapping" />, <TopologicalButton key="topological" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

/**
 * This component is responsible for managing the drawing of ISG Blockers.
 */
const BlockersDrawPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);

  // Configure drawing options with layer-specific styles
  const defaultOptions: MapboxDrawOptions = useMemo(
    () => ({
      snap: true,
      snapOptions: {
        snapVertexPriorityDistance: 0.000025,
      },
      editOptions: {
        allLayers: [
          {
            ID: ISGLayerIds.blockers,
            GeometryType: "LineString",
            // Maybe relate what this returns to the attributes defined.
            getFeatureProperties: () => ({ layerId: ISGLayerIds.blockers, Tier1: true, Tier2: false, Tier2_MST: false, Tier3: false }),
            Attributes: {
              Tier1: { Type: "BOOLEAN" },
              Tier2: { Type: "BOOLEAN" },
              Tier2_MST: { Type: "BOOLEAN" },
              Tier3: { Type: "BOOLEAN" },
            },
            PromptAttributesOnCreate: true,
          },
        ],
        editingLayerId: ISGLayerIds.blockers, // Default selected layer
        editingLayerIds: [ISGLayerIds.blockers], // Default editing layers (enables edit mode)
      },
    }),
    []
  );

  useMount(() => {
    dispatch(setEditMode(EditMode.edit));
  });

  return (
    <MultiLayerEditProvider versionId={versionId} options={defaultOptions} config={allToolConfig}>
      <DataPrepDrawContent />
    </MultiLayerEditProvider>
  );
};

export default BlockersDrawPanel;
